import React, { useEffect, useState } from 'react';
import { AlertCircle, Layers, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import SelectorModeloIA from './SelectorModeloIA';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { aiSettingsAPI } from '../api';

const AIModulosProveedor = () => {
  const [modulos, setModulos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [noDisponible, setNoDisponible] = useState(false);
  const [guardando, setGuardando] = useState('');
  const [modelos, setModelos] = useState({});

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      const { data } = await aiSettingsAPI.getModulos();
      setModulos(data.modulos || []);
      setProveedores(data.proveedores || []);
      const m = {};
      (data.modulos || []).forEach((x) => { m[x.id] = x.model || ''; });
      setModelos(m);
    } catch (e) {
      if (e.response?.status === 404) setNoDisponible(true);
    } finally {
      setCargando(false);
    }
  };

  const guardar = async (modulo, provider, model) => {
    setGuardando(modulo);
    try {
      const { data } = await aiSettingsAPI.updateModulo({ modulo, provider, model });
      setModulos(data.modulos || []);
      toast({ title: 'Guardado', description: 'Esa función ya usa el proveedor elegido.' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.detail || 'No se pudo guardar',
        variant: 'destructive',
      });
      await cargar();
    } finally {
      setGuardando('');
    }
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (noDisponible) {
    return (
      <Card data-testid="ai-modulos-no-disponible" className="border-2 border-gray-200">
        <CardContent className="pt-6 text-sm text-gray-600 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
          Elegir proveedor por función necesita una actualización del servidor que todavía no
          está publicada. En cuanto se aplique, este recuadro se activará solo.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ai-modulos-card" className="border-2 border-indigo-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-600" />
          Qué proveedor usa cada función
        </CardTitle>
        <CardDescription>
          Déjalo en «Automático» y cada función usará la clave que tengas configurada. Fija un
          proveedor solo si quieres que una función concreta use otro modelo, por calidad o por
          coste.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {modulos.map((m) => {
          const esAuto = !m.provider || m.provider === 'auto';
          return (
            <div
              key={m.id}
              data-testid={`ai-modulo-fila-${m.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-3 rounded-xl border border-gray-200"
            >
              <div className="md:col-span-4 flex items-center gap-2">
                <span className="font-medium text-sm">{m.nombre}</span>
                {esAuto && (
                  <Badge variant="outline" className="text-[10px]">auto</Badge>
                )}
              </div>

              <div className="md:col-span-4">
                <select
                  data-testid={`ai-modulo-proveedor-${m.id}`}
                  value={m.provider || 'auto'}
                  disabled={guardando === m.id}
                  onChange={(e) => guardar(m.id, e.target.value, modelos[m.id] || '')}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-4">
                <SelectorModeloIA
                  proveedor={m.provider}
                  value={modelos[m.id] ?? ''}
                  disabled={guardando === m.id}
                  testId={`ai-modulo-modelo-${m.id}`}
                  onChange={(valor) => {
                    setModelos((s) => ({ ...s, [m.id]: valor }));
                    guardar(m.id, m.provider, valor);
                  }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AIModulosProveedor;
